function initializeTool(root) {
  const tool = root.dataset.tool;
  const form = root.querySelector('[data-tool-form]');
  if (!tool || !form) return;
  const messages = JSON.parse(root.querySelector('[data-tool-messages]').textContent);
  const result = root.querySelector('[data-tool-result]');
  const output = root.querySelector('[data-tool-output]');
  const actions = root.querySelector('[data-tool-downloads]');
  const note = root.querySelector('[data-tool-result-note]');
  const status = root.querySelector('[data-tool-status]');
  const urls = new Set();
  let revision = 0;

  function clearResult() {
    revision += 1;
    result.hidden = true;
    output.replaceChildren();
    actions.replaceChildren();
    note.textContent = '';
    for (const url of urls) URL.revokeObjectURL(url);
    urls.clear();
  }

  function message(text, isError = false) {
    status.textContent = text;
    status.classList.toggle('is-error', isError);
  }

  function field(name) {
    return form.elements.namedItem(name);
  }

  function phone(value) {
    const normalized = String(value).trim().replace(/[\s().-]/g, '');
    if (!/^\+?\d{6,18}$/.test(normalized)) throw new Error(messages.phone_error);
    return normalized;
  }

  function canvasBlob(canvas) {
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error(messages.error)), 'image/png');
    });
  }

  function download(bytes, filename, type, current) {
    if (current !== revision) return;
    output.querySelectorAll('a[download]').forEach((previous) => {
      if (previous.download !== filename) return;
      URL.revokeObjectURL(previous.href);
      urls.delete(previous.href);
      previous.remove();
    });
    const url = URL.createObjectURL(new Blob([bytes], { type }));
    urls.add(url);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.className = 'origin-tool__download';
    link.textContent = filename;
    output.append(link);
    link.click();
  }

  function action(label, callback) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'button button--quiet';
    button.textContent = label;
    button.addEventListener('click', async () => {
      button.disabled = true;
      try { await callback(button); }
      catch (error) { message(error.message || messages.error, true); }
      finally { button.disabled = false; }
    });
    actions.append(button);
  }

  function showCanvas(canvas) {
    canvas.setAttribute('aria-label', result.querySelector('h2').textContent);
    output.append(canvas);
  }

  function pngAction(canvas, filename) {
    const current = revision;
    action(messages.png, async () => download(await canvasBlob(canvas), filename, 'image/png', current));
  }

  function copyAction(value) {
    action(messages.copy, async () => {
      try {
        await navigator.clipboard.writeText(value);
        message(messages.copied);
      } catch { throw new Error(messages.copy_error); }
    });
  }

  function textResult(text) {
    const paragraph = document.createElement('p');
    paragraph.textContent = text;
    output.append(paragraph);
  }

  function qrPayload(data) {
    if (data.type === 'link') {
      let url;
      try { url = new URL(data.url); } catch { throw new Error(messages.url_error); }
      if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password) throw new Error(messages.url_error);
      return url.href;
    }
    if (data.type === 'phone') return 'tel:' + phone(data.phone);
    if (data.type === 'contact') {
      const escape = (value) => String(value || '').replace(/\\/g, '\\\\').replace(/[\r\n]+/g, ' ').replace(/[;,]/g, '\\$&');
      const lines = ['BEGIN:VCARD', 'VERSION:3.0', 'FN:' + escape(data.contact_name), 'TEL;TYPE=CELL:' + phone(data.contact_phone)];
      if (data.email) lines.push('EMAIL:' + escape(data.email));
      return [...lines, 'END:VCARD'].join('\r\n');
    }
    if (!data.ssid || !['WPA', 'WEP', 'nopass'].includes(data.security)) throw new Error(messages.wifi_error);
    const escape = (value) => String(value || '').replace(/[\\;,:\"]/g, '\\$&');
    return `WIFI:T:${data.security};S:${escape(data.ssid)};P:${escape(data.wifi_password)};H:${data.hidden === 'on'};;`;
  }

  async function createQr(data, current) {
    const payload = qrPayload(data);
    const engine = await import(root.dataset.qrEngine);
    const canvas = document.createElement('canvas');
    await engine.canvas(canvas, payload);
    if (current !== revision) return;
    showCanvas(canvas);
    pngAction(canvas, 'elternanker-qr.png');
    action(messages.svg, async () => download(await engine.svg(payload), 'elternanker-qr.svg', 'image/svg+xml', current));
    note.textContent = messages.qr_note;
  }

  function drawText(context, text, x, y, width, size = 42, weight = 500) {
    context.font = `${weight} ${size}px Manrope, Arial, sans-serif`;
    context.fillText(text, x, y, width);
  }

  async function createCards(data, current) {
    const contacts = [];
    if (tool === 'familienkarte') {
      for (let index = 0; index < 4; index += 1) {
        const name = data['name' + index]?.trim();
        const number = data['phone' + index]?.trim();
        if (!name && !number) continue;
        if (!name || !number) throw new Error(messages.pair_error);
        contacts.push({ name, phone: phone(number) });
      }
    } else {
      contacts.push({ name: data.name.trim(), phone: data.phone ? phone(data.phone) : '' });
    }
    if (data.qr && !contacts[0]?.phone) throw new Error(messages.qr_phone_error);
    const size = tool === 'etiketten' ? data.size.split(',').map(Number) : [85.6, 54];
    const count = Number(data.copies);
    if (!Number.isInteger(count) || count < 1 || count > 40) throw new Error(messages.error);
    const canvas = document.createElement('canvas');
    canvas.width = 1200;
    canvas.height = Math.round(canvas.width * size[1] / size[0]);
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#FAFAF6';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#9D3027';
    ctx.fillRect(48, 50, 90, 5);
    ctx.fillStyle = '#20231F';
    drawText(ctx, messages.card_heading, 48, 115, 1104, 36, 700);
    const contentWidth = data.qr ? 760 : 1104;
    const lineGap = tool === 'familienkarte' ? 116 : 180;
    contacts.forEach((contact, index) => {
      const y = 205 + index * lineGap;
      drawText(ctx, contact.name, 48, y, contentWidth, 40, 700);
      if (contact.phone) drawText(ctx, contact.phone, 48, y + 50, contentWidth, 40);
    });
    if (data.qr) {
      const qr = document.createElement('canvas');
      await (await import(root.dataset.qrEngine)).canvas(qr, 'tel:' + contacts[0].phone);
      ctx.drawImage(qr, 870, (canvas.height - 270) / 2, 270, 270);
    }
    drawText(ctx, messages.card_thanks, 48, canvas.height - 50, 1104, 27);
    if (current !== revision) return;
    showCanvas(canvas);
    pngAction(canvas, 'elternanker-kontaktkarte.png');
    action(messages.pdf, async () => {
      const bytes = new Uint8Array(await (await canvasBlob(canvas)).arrayBuffer());
      const pdf = await (await import(root.dataset.pdfEngine)).cardPDF(bytes, size[0], size[1], count);
      download(pdf, 'elternanker-karten.pdf', 'application/pdf', current);
    });
    note.textContent = messages.print_note;
  }

  async function readQr(current) {
    const file = field('image').files[0];
    if (!file || !['image/png', 'image/jpeg', 'image/webp'].includes(file.type) || file.size > 10 * 1024 * 1024) throw new Error(messages.image_error);
    const bitmap = await createImageBitmap(file);
    let decoded;
    try {
      const scale = Math.min(1, 2048 / Math.max(bitmap.width, bitmap.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(bitmap.width * scale);
      canvas.height = Math.round(bitmap.height * scale);
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      decoded = (await import(root.dataset.readerEngine)).decode(ctx.getImageData(0, 0, canvas.width, canvas.height).data, canvas.width, canvas.height);
    } finally { bitmap.close(); }
    if (!decoded) throw new Error(messages.read_error);
    if (current !== revision) return;
    const text = document.createElement('textarea');
    text.readOnly = true;
    text.rows = 6;
    text.setAttribute('aria-label', result.querySelector('h2').textContent);
    text.value = decoded;
    output.append(text);
    copyAction(decoded);
    note.textContent = messages.reader_result;
  }

  function randomIndex(limit) {
    if (!globalThis.crypto?.getRandomValues) throw new Error(messages.random_error);
    const ceiling = Math.floor(4294967296 / limit) * limit;
    const bytes = new Uint32Array(1);
    do { crypto.getRandomValues(bytes); } while (bytes[0] >= ceiling);
    return bytes[0] % limit;
  }

  function createPassword(data) {
    const sets = { upper: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ', lower: 'abcdefghijklmnopqrstuvwxyz', digits: '0123456789', symbols: '!#$%&()*+,-./:;<=>?@[]^_{|}~' };
    const selected = Object.entries(sets).filter(([key]) => data[key]).map(([, value]) => value);
    const length = Number(data.length);
    if (!selected.length || !Number.isInteger(length) || length < 4 || length > 128) throw new Error(messages.password_error);
    const pool = selected.join('');
    const characters = selected.map((set) => set[randomIndex(set.length)]);
    while (characters.length < length) characters.push(pool[randomIndex(pool.length)]);
    for (let index = characters.length - 1; index > 0; index -= 1) {
      const other = randomIndex(index + 1);
      [characters[index], characters[other]] = [characters[other], characters[index]];
    }
    const value = characters.join('');
    const input = document.createElement('input');
    input.type = 'password';
    input.readOnly = true;
    input.autocomplete = 'off';
    input.value = value;
    input.setAttribute('aria-label', result.querySelector('h2').textContent);
    output.append(input);
    action(messages.show_password, (button) => {
      const visible = input.type === 'password';
      input.type = visible ? 'text' : 'password';
      button.textContent = visible ? messages.hide_password : messages.show_password;
      button.setAttribute('aria-pressed', String(visible));
    });
    copyAction(value);
    note.textContent = messages.privacy_clipboard;
  }

  function fillPackingList() {
    const list = root.querySelector('[data-packing-list]');
    if (!list) return;
    list.replaceChildren();
    for (const text of messages['list_' + field('occasion').value].split('|')) {
      const label = document.createElement('label');
      label.className = 'origin-tool__check';
      const input = document.createElement('input');
      input.type = 'checkbox';
      input.dataset.packingText = text;
      label.append(input, ' ' + text);
      list.append(label);
    }
  }

  function createPacking(data) {
    const current = revision;
    const extra = data.extra.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
    if (extra.length > 25 || extra.some((line) => line.length > 80)) throw new Error(messages.extra_error);
    const items = [...root.querySelectorAll('[data-packing-text]')].map((item) => ({ text: item.dataset.packingText, checked: item.checked }));
    items.push(...extra.map((text) => ({ text, checked: false })));
    const canvases = [];
    for (let offset = 0; offset < items.length; offset += 20) {
      const canvas = document.createElement('canvas');
      canvas.width = 1400;
      canvas.height = 2040;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#20231F';
      drawText(ctx, data.title, 80, 150, 1240, 64, 700);
      items.slice(offset, offset + 20).forEach((item, index) => {
        const y = 280 + index * 76;
        ctx.strokeStyle = '#20231F';
        ctx.lineWidth = 3;
        ctx.strokeRect(82, y - 26, 28, 28);
        if (item.checked) {
          ctx.beginPath();
          ctx.moveTo(87, y - 13);
          ctx.lineTo(94, y - 6);
          ctx.lineTo(105, y - 21);
          ctx.stroke();
        }
        drawText(ctx, item.text, 140, y, 1180, 34);
      });
      drawText(ctx, 'eltern-anker.de', 80, 1980, 1240, 26);
      showCanvas(canvas);
      canvases.push(canvas);
    }
    action(messages.pdf, async () => {
      const images = await Promise.all(canvases.map(async (canvas) => (await canvasBlob(canvas)).arrayBuffer()));
      download(await (await import(root.dataset.pdfEngine)).pagePDF(images), 'elternanker-packliste.pdf', 'application/pdf', current);
    });
    note.textContent = messages.print_note;
  }

  function showAdvice(data) {
    let type = data.preference;
    if (data.place === 'jacket') type = 'jacket';
    const title = document.createElement('h3');
    title.textContent = messages['advice_' + type + '_title'];
    output.append(title);
    textResult(messages['advice_' + type]);
  }

  function showNfc(data) {
    const steps = document.createElement('ol');
    steps.className = 'origin-tool__nfc';
    for (const text of messages['nfc_' + data.device].split('|')) {
      const item = document.createElement('li');
      item.textContent = text;
      steps.append(item);
    }
    output.append(steps);
    note.textContent = messages.nfc_result;
  }

  function updateQrFields() {
    if (tool !== 'qr-code') return;
    root.querySelectorAll('[data-qr-type]').forEach((group) => {
      group.hidden = group.dataset.qrType !== field('type').value;
      group.disabled = group.hidden;
    });
    field('wifi_password').disabled = field('security').value === 'nopass';
    field('wifi_password').required = !field('wifi_password').disabled;
  }

  form.addEventListener('input', () => { clearResult(); message(''); });
  form.addEventListener('change', (event) => {
    clearResult();
    updateQrFields();
    if (event.target.name === 'occasion') fillPackingList();
  });
  form.addEventListener('reset', () => {
    clearResult();
    message(messages.cleared);
    setTimeout(() => { updateQrFields(); fillPackingList(); }, 0);
  });
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (!form.reportValidity()) return;
    clearResult();
    const current = revision;
    const data = Object.fromEntries(new FormData(form));
    const submit = form.querySelector('[type=submit]');
    submit.disabled = true;
    try {
      if (tool === 'qr-code') await createQr(data, current);
      else if (['kontaktkarte', 'etiketten', 'familienkarte'].includes(tool)) await createCards(data, current);
      else if (tool === 'qr-lesen') await readQr(current);
      else if (tool === 'passwort') createPassword(data);
      else if (tool === 'packliste') createPacking(data);
      else if (tool === 'befestigung') showAdvice(data);
      else if (tool === 'nfc-test') showNfc(data);
      if (current !== revision) return;
      result.hidden = false;
      message(messages.ready);
      result.querySelector('h2').focus();
    } catch (error) {
      if (current === revision) message(error.message || messages.error, true);
    } finally { submit.disabled = false; }
  });
  updateQrFields();
  fillPackingList();
}

document.querySelectorAll('[data-origin-tools]').forEach(initializeTool);
